import { useState } from "react";
import "./KycUpload.css";
import KycDocumentModel from "../../model/KycDocumentModel";
import AlertMsg from "../AlertMsg/AlertMsg";
import AlertMsgError from "../AlertMsg/AlertMsgError";

const KycUpload = ({ userId, documentType, onClose, onSuccess }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [visibleError, setVisibleError] = useState(false);
    const [msj, setMsj] = useState("");

    const documentLabels = {
        id_front: "ID Front (Passport or Driver's License)",
        id_back: "ID Back (Passport or Driver's License)",
        proof_address: "Proof of Address (Utility Bill or Bank Statement)",
        selfie: "Selfie with ID"
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tamaño
            if (file.size > 5 * 1024 * 1024) {
                setMsj("File size exceeds 5MB limit");
                setVisibleError(true);
                return;
            }

            // Validar tipo
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
            if (!validTypes.includes(file.type)) {
                setMsj("Invalid file type. Only JPG, PNG and PDF are allowed");
                setVisibleError(true);
                return;
            }

            setSelectedFile(file);

            // Crear preview solo para imágenes
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreviewUrl(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setPreviewUrl(null);
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setMsj("Please select a file first");
            setVisibleError(true);
            return;
        }

        setUploading(true);
        try {
            const kycModel = new KycDocumentModel(userId, documentType, selectedFile);
            await kycModel.uploadDocument();
            
            setMsj("Document uploaded successfully! Pending review.");
            setVisible(true);
            
            setTimeout(() => {
                if (onSuccess) onSuccess();
                if (onClose) onClose();
            }, 2000);
        } catch (error) {
            console.error("Error uploading document:", error);
            setMsj(error.message || "Error uploading document");
            setVisibleError(true);
        } finally {
            setUploading(false);
        }
    };

    const handleSelectClick = () => {
        document.getElementById('kycFileInput').click();
    };

    return (
        <div className="kyc-upload-modal">
            <AlertMsg visible={visible} setVisible={setVisible} texto={msj} />
            <AlertMsgError visible={visibleError} setVisible={setVisibleError} texto={msj} />
            
            <div className="kyc-overlay" onClick={onClose}></div>
            <div className="kyc-upload-content">
                <div className="kyc-upload-header">
                    <h3>Upload {documentLabels[documentType]}</h3>
                    <button onClick={onClose} className="close-btn">
                        <i className="bi bi-x"></i>
                    </button>
                </div>

                <div className="kyc-upload-body">
                    <div className="kyc-instructions">
                        <p><i className="bi bi-info-circle"></i> Please ensure:</p>
                        <ul>
                            <li>Document is clear and readable</li>
                            <li>All corners of the document are visible</li>
                            <li>File size is less than 5MB</li>
                            <li>Format: JPG, PNG or PDF</li>
                        </ul>
                    </div>

                    <div className="kyc-preview">
                        {previewUrl ? (
                            <img src={previewUrl} alt="Preview" />
                        ) : selectedFile?.type === 'application/pdf' ? (
                            <div className="pdf-indicator">
                                <i className="bi bi-file-pdf"></i>
                                <p>{selectedFile.name}</p>
                            </div>
                        ) : (
                            <div className="no-preview">
                                <i className="bi bi-cloud-upload"></i>
                                <p>No file selected</p>
                            </div>
                        )}
                    </div>

                    <input
                        id="kycFileInput"
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />

                    <div className="kyc-actions">
                        {selectedFile ? (
                            <>
                                <button 
                                    onClick={handleSelectClick} 
                                    className="btn-secondary"
                                    disabled={uploading}
                                >
                                    <i className="bi bi-arrow-repeat"></i> Change File
                                </button>
                                <button 
                                    onClick={handleUpload} 
                                    className="btn-primary"
                                    disabled={uploading}
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-small"></span>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-upload"></i> Upload Document
                                        </>
                                    )}
                                </button>
                            </>
                        ) : (
                            <button 
                                onClick={handleSelectClick} 
                                className="btn-primary"
                            >
                                <i className="bi bi-file-earmark-plus"></i> Select File
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KycUpload;
